import { isOneOf } from '@shared/validation/is-one-of';
import { literalValues } from "@shared/validation/literal-values";

export const SUBMISSION_CHANNEL = {
  WEB_PUBLIC: 'web_public',
  WEB_DASHBOARD: 'web_dashboard',
  API_EXTERNAL: 'api_external',
  CHATBOT: 'chatbot',
} as const

export type SubmissionChannel = (typeof SUBMISSION_CHANNEL)[keyof typeof SUBMISSION_CHANNEL]

export const SUBMISSION_CHANNEL_LABEL: Record<SubmissionChannel, string> = {
  [SUBMISSION_CHANNEL.WEB_PUBLIC]: 'Public website',
  [SUBMISSION_CHANNEL.WEB_DASHBOARD]: 'Customer dashboard',
  [SUBMISSION_CHANNEL.API_EXTERNAL]: 'External API',
  [SUBMISSION_CHANNEL.CHATBOT]: 'Chatbot',
}

export const SUBMISSION_CHANNELS = literalValues(SUBMISSION_CHANNEL)

export function isValidSubmissionChannel(value: unknown): value is SubmissionChannel {
  return typeof value === 'string' && isOneOf(SUBMISSION_CHANNELS, value)
}

export const SUBMISSION_ORIGIN = {
  LISTING_QUOTE_FORM: 'listing_quote_form',
  LISTING_RECYCLE_BID_FORM: 'listing_recycle_bid_form',
  DASHBOARD_QUICK_QUOTE: 'dashboard_quick_quote',
  RFQ_WIZARD: 'rfq_wizard',
  DASHBOARD_RFQ: 'dashboard_rfq',
  CONTACT_FORM: 'contact_form',
  CONTACT_FORM_CONVERTED: 'contact_form_converted',
  CHATBOT: 'chatbot',
  ADMIN_MANUAL: 'admin_manual',
  ADMIN_QUOTE: 'admin_quote',
  OUTREACH_PROSPECT: 'outreach_prospect',
  PACKAGING_DESIGNER: 'packaging_designer',
  SOCIAL_POST_CONVERTER: 'social_post_converter',
} as const

export type SubmissionOrigin = (typeof SUBMISSION_ORIGIN)[keyof typeof SUBMISSION_ORIGIN]

export const STAFF_SUBMISSION_ORIGINS: ReadonlySet<string> = new Set<SubmissionOrigin>([
  SUBMISSION_ORIGIN.ADMIN_MANUAL,
  SUBMISSION_ORIGIN.ADMIN_QUOTE,
  SUBMISSION_ORIGIN.OUTREACH_PROSPECT,
  SUBMISSION_ORIGIN.SOCIAL_POST_CONVERTER,
])

export const SUBMISSION_ORIGIN_LABEL: Record<string, string> = {
  [SUBMISSION_ORIGIN.LISTING_QUOTE_FORM]: 'Listing quote form',
  [SUBMISSION_ORIGIN.LISTING_RECYCLE_BID_FORM]: 'Listing recycle bid form',
  [SUBMISSION_ORIGIN.DASHBOARD_QUICK_QUOTE]: 'Dashboard quick quote',
  [SUBMISSION_ORIGIN.RFQ_WIZARD]: 'Public request-a-quote wizard',
  [SUBMISSION_ORIGIN.DASHBOARD_RFQ]: 'Dashboard request-a-quote',
  [SUBMISSION_ORIGIN.CONTACT_FORM]: 'Contact form',
  [SUBMISSION_ORIGIN.CONTACT_FORM_CONVERTED]: 'Contact form (converted ticket)',
  [SUBMISSION_ORIGIN.CHATBOT]: 'Chatbot',
  [SUBMISSION_ORIGIN.ADMIN_MANUAL]: 'Created by admin',
  [SUBMISSION_ORIGIN.ADMIN_QUOTE]: 'Created from admin quote',
  [SUBMISSION_ORIGIN.PACKAGING_DESIGNER]: 'Packaging Designer',
  [SUBMISSION_ORIGIN.SOCIAL_POST_CONVERTER]: 'Social post converter',
}

export function submissionOriginLabel(origin: string | undefined | null): string | undefined {
  if (!origin) return undefined
  return SUBMISSION_ORIGIN_LABEL[origin] ?? origin
}
